/**
 * PRO Work Units (WU) — the single frontend source of truth for the
 * work-unit denomination (2026-08 pricing migration).
 *
 * Prest0 prices in finished work, not tokens. Every user-facing capacity,
 * usage, and balance surface displays PRO Work Units; raw provider token
 * counts are internal-only and must never render in UI (2026-08-13 Miles
 * directive — supersedes the tokens-in/tokens-out displays).
 *
 * The Standard Rate is the published on-demand · overtime · top-up
 * conversion: exactly $2.18 per Work Unit. Committed plans buy WU cheaper
 * (see WU_PRICING ladder), but every conversion between a charged dollar
 * amount and displayed WU happens at the Standard Rate until the backend
 * stamps a server-authoritative wu_amount per turn (Phase 3 of
 * PLAN-pro-work-units-migration-20260813.md).
 *
 * Do NOT duplicate these constants. Wallet, chat, and Activity surfaces all
 * import from here so the displayed numbers reconcile with each other.
 */

/** Published Standard Rate: on-demand / overtime / top-ups. Exactly $2.18. */
export const STANDARD_RATE_USD_PER_WU = 2.18;

/**
 * Compact unit label rendered next to amounts (e.g. "142.5 pr0").
 * 2026-08-13 owner directive: the abbreviation is lowercase pr0 (p-r-zero,
 * matching the brand lockup) — never "WU", and never uppercased except
 * where a sentence-start demands it. In rich UI the zero renders as the
 * prest0 logo via the Pr0Unit component; this string is the plain-text
 * form for aria-labels, CSV, and copied values. PUBLIC surfaces spell out
 * "PRO Work Units" on first mention (WU_FULL_NAME) and use pr0 only after
 * introducing it.
 */
export const WU_LABEL = 'pr0';

/** Lowercase text form matching the brand lockup ("pr0"). */
export const WU_LABEL_LOWER = 'pr0';

/** Full product name for headings and first mentions. */
export const WU_FULL_NAME = 'PRO Work Units';

/** Singular form for copy. */
export const WU_SINGULAR = 'PRO Work Unit';

/** Monthly allocation per hired Prest0n Professional. */
export const MONTHLY_WU_PER_PRESTON = 2200;

/**
 * The published commitment ladder (per Prest0n Professional, per month).
 * Invariant (pricing master chart rule one): price per WU only falls as
 * commitment rises — keep this monotonic through every future change.
 */
export const WU_PRICING = {
  /** List price — on-demand · overtime · top-ups. */
  standard: { monthlyUsd: 4800, usdPerWu: 2.18, label: 'standard rate' },
  /** Monthly hire — cancel anytime. */
  monthly: { monthlyUsd: 3750, usdPerWu: 1.7, savePct: 22, label: 'monthly hire' },
  /** Annual hire — the headline number. */
  annual: { monthlyUsd: 3200, usdPerWu: 1.45, savePct: 33, label: 'annual hire' },
  /** Annual, 3+ Prest0ns. */
  annualTeam3: { monthlyUsd: 2950, usdPerWu: 1.34, savePct: 38.5, label: 'annual · 3+ team' },
  /** Annual, 5+ Prest0ns. */
  annualTeam5: { monthlyUsd: 2750, usdPerWu: 1.25, savePct: 43, label: 'annual · 5+ team' },
  /** Overtime is time-and-a-half on the committed rate — which is the Standard Rate. */
  overtimeMultiplier: 1.5,
  /** Sprint blocks: bulk top-ups of 1,100+ WU get 10% off the Standard Rate, 30-day expiry. */
  sprintBlockMinWu: 1100,
  sprintBlockDiscountPct: 10,
  sprintBlockExpiryDays: 30,
  /**
   * Prepay the 12 months up front: an ADDITIONAL 20% off the seat-count rate
   * (2026-08-16 owner directive — it is a discount, never bonus work units;
   * the monthly allocation stays 2,200 pr0 per Prest0n).
   */
  prepayDiscountPct: 20,
} as const;

// ── The one hire calculator (2026-08-16 owner directive) ────────────────────
// Tiers are never shown as a table anywhere: the buyer toggles cadence and
// seat count, and the rate + % discount recompute live. Both /pricing and
// /subscription price through this single function so they can never
// disagree — and neither can the backend TIER_CONFIG, whose amounts these
// mirror (functions/src/subscriptions.ts).

export type HireCadence = 'monthly' | 'annual' | 'prepay';

/** The seat-count rung on the annual ladder (rates fall at 3 and at 5). */
export function annualSeatRung(seats: number): {
  key: 'annual' | 'annualTeam3' | 'annualTeam5';
  monthlyUsd: number;
  usdPerWu: number;
} {
  if (seats >= 5) return { key: 'annualTeam5', ...WU_PRICING.annualTeam5 };
  if (seats >= 3) return { key: 'annualTeam3', ...WU_PRICING.annualTeam3 };
  return { key: 'annual', ...WU_PRICING.annual };
}

export interface HireQuote {
  /** Backend tier id (functions/src/subscriptions.ts TIER_CONFIG). */
  tier:
    | 'wu_monthly'
    | 'wu_annual'
    | 'wu_annual_team3'
    | 'wu_annual_team5'
    | 'wu_prepay'
    | 'wu_prepay_team3'
    | 'wu_prepay_team5';
  /** Effective monthly-equivalent price per Prest0n (prepay: after the 20%). */
  perSeatMonthlyUsd: number;
  /** Effective committed $ per pr0. */
  usdPerWu: number;
  /** % saved vs the Standard Rate — the headline number. */
  savePct: number;
  /** pr0 per Prest0n per month — always 2,200; prepay is a discount, not capacity. */
  wuPerSeat: number;
  totalMonthlyUsd: number;
  totalWuMonthly: number;
  /** Prepay only: the single payment for the year. */
  upfrontTotalUsd: number | null;
}

export function hireQuote(cadence: HireCadence, seats: number): HireQuote {
  const n = Math.max(1, Math.floor(seats) || 1);
  if (cadence === 'monthly') {
    return {
      tier: 'wu_monthly',
      perSeatMonthlyUsd: WU_PRICING.monthly.monthlyUsd,
      usdPerWu: WU_PRICING.monthly.usdPerWu,
      savePct: WU_PRICING.monthly.savePct,
      wuPerSeat: MONTHLY_WU_PER_PRESTON,
      totalMonthlyUsd: WU_PRICING.monthly.monthlyUsd * n,
      totalWuMonthly: MONTHLY_WU_PER_PRESTON * n,
      upfrontTotalUsd: null,
    };
  }
  const rung = annualSeatRung(n);
  const prepay = cadence === 'prepay';
  const factor = prepay ? 1 - WU_PRICING.prepayDiscountPct / 100 : 1;
  const perSeat = Math.round(rung.monthlyUsd * factor);
  const savePct = Math.round((1 - perSeat / WU_PRICING.standard.monthlyUsd) * 100);
  const tier = prepay
    ? rung.key === 'annualTeam5' ? 'wu_prepay_team5' : rung.key === 'annualTeam3' ? 'wu_prepay_team3' : 'wu_prepay'
    : rung.key === 'annualTeam5' ? 'wu_annual_team5' : rung.key === 'annualTeam3' ? 'wu_annual_team3' : 'wu_annual';
  return {
    tier,
    perSeatMonthlyUsd: perSeat,
    usdPerWu: Number((rung.usdPerWu * factor).toFixed(2)),
    savePct,
    wuPerSeat: MONTHLY_WU_PER_PRESTON,
    totalMonthlyUsd: perSeat * n,
    totalWuMonthly: MONTHLY_WU_PER_PRESTON * n,
    upfrontTotalUsd: prepay ? perSeat * 12 * n : null,
  };
}

/**
 * The published Work Rate Card — flat WU per finished task type. Rates never
 * vary with difficulty, retries, or thinking depth; overruns are 0 WU always.
 *
 * ── PROVENANCE (2026-08-19 rate-card recalibration) ────────────────────────
 * The original rates were set in the 2026-08-13 pricing migration BEFORE any
 * consumption data existed. They were far too high. Measured against the
 * production wallet ledger (`scripts/pr0-consumption-analysis.mjs`, run by
 * `.github/workflows/pr0-consumption-telemetry.yml` — complete scan, 5,749
 * expense rows, 2026-01-28 .. 2026-08-19):
 *
 *   task type            published   measured p50      measured p90     n
 *   deep-research memo      40        5.30 (2.29/30d)     7.36        156 sessions (all-time)
 *   drafted document         8        0.29                2.25         20 (all-time)
 *   translation              6        0.25 (0.70/30d)     2.52 (30d)   33 (30d)
 *   document intake/file     2        0.31                2.05          7 (all-time)
 *   task turn                1        0.13                0.97        227 (30d; all-time n
 *                                                                      not broken out — the
 *                                                                      percentiles here ARE
 *                                                                      the all-time ones)
 *
 * So the card overstated a deep-research memo by 5.4x at p90 and 7.5x at p50 —
 * a customer reading /pricing believed 2,200 pr0 bought 55 memos when it
 * actually buys ~275 at the new rate and ~400+ at measured p50.
 *
 * NEW RATES ARE SET AT ≈p90, NOT p50, deliberately: published capacity is a
 * promise the platform has to keep on a bad month, not a typical-case boast.
 * p90 is also the stable statistic here — it came out at 7.36 for deep
 * research in BOTH the 30-day window (n=14) and the all-time ledger (n=156),
 * while p50 moved from 5.30 to 2.29 as the model ladder got cheaper. Setting
 * rates at p50 would publish ~400 memos/month and put half of all runs over
 * the line the moment prices move.
 *
 * The tail beyond p90 is Prest0's risk, by design — that is exactly what the
 * "overruns are ours, always" row means. Observed worst case for a single
 * deep-research deliverable in the full ledger was 25.76 pr0.
 *
 * ONE STATED EXCEPTION TO THE p90 RULE — the task turn. Every other row clears
 * its 30-day p90 (memo 8 ≥ 7.36, document 3 ≥ 2.84, translation 3 ≥ 2.52,
 * intake 2 ≈ 2.05). The task turn stays at 1, which is its ALL-TIME p90 (0.97)
 * but is BELOW its 30-day p90 of 2.05. Deliberate: "one task turn = one work
 * unit" is the anchor that makes the unit legible, and moving it to 2 would
 * halve published task capacity while making the card harder to explain than
 * the thing it prices. The shortfall on heavy turns rides "overruns are ours".
 *
 * RE-MEASURE BEFORE CHANGING ANY RATE BELOW: re-run the telemetry workflow.
 * Do not hand-edit these numbers from intuition — that is how they got wrong.
 */
export const WORK_RATE_CARD = [
  {
    task: 'deep-research memo',
    wu: 8,
    unit: 'per memo',
    receive: 'cited long-form memo with sources — PDF + Word, in minutes',
  },
  {
    task: 'drafted document',
    wu: 3,
    unit: 'per document',
    receive: 'contract, agreement, demand letter, memo — attorney-review flagged where legal',
  },
  {
    task: 'certified-style translation',
    wu: 3,
    unit: 'per document',
    receive: 'per document, EN↔ES, PDF + Word always',
  },
  {
    task: 'document intake & analysis',
    wu: 2,
    unit: 'per file',
    receive: 'OCR, reading, indexing — batches returned as one deliverable',
  },
  {
    // 2026-08-19: task turns measure p50 0.13 / p90 0.97 — 1 pr0 is the one
    // rate on this card that was already right, so it is unchanged.
    // ⚠️ VOICE IS THE EXCEPTION AND IS KNOWINGLY UNDER-PRICED HERE. The single
    // voice row in the ledger metered 8.50 pr0, and tier-10-voice has NO entry
    // in MODEL_PRICING at all (src/config/aiModelPricing.ts) — every voice
    // generation falls through to the 'unknown' $1/$5-per-million floor, so
    // even that 8.50 is a placeholder, not a real provider rate. n=1 is far
    // too thin to publish a number from. Price tier-10 and re-measure BEFORE
    // splitting voice onto its own row; until then the gap is absorbed under
    // "overruns are ours". See the report for the exposure math.
    task: 'task turn · Routine run · voice minute',
    wu: 1,
    unit: 'each',
    receive: 'end to end, however many internal steps it takes',
  },
  {
    // ⚠️ 2026-08-19: THE WORST-PRICED ROW ON THIS CARD — worse than voice, and
    // unchanged only because there is nothing to change it TO. No ledger rows
    // classify as sub-hourly monitoring, so there is no measured sample.
    // The arithmetic is not reassuring: every Routine run is a full billed turn
    // (measured p50 0.13, p90 0.97 pr0), so a 5-minute Routine is 288 runs/day
    // ≈ 37-280 pr0/day against the 1 pr0 published here.
    // It is NOT, however, ungoverned — three limits bound it, and any writeup
    // that calls it "unbounded" is wrong: MIN_INTERVAL_SECONDS=15 is the clamp,
    // but ROUTINE_CONFIRMATION_THRESHOLD_SECONDS=30*60 means every sub-30-minute
    // cadence activates only after the user confirms a presented $/hour + $/day
    // estimate, and MAX_ROUTINES_PER_ACCOUNT=20 caps the fan-out
    // (functions/src/prest0nSession/routines.ts:272, :281, :294).
    // So the defect is a PRICING one, not a runaway one: the card's 1 pr0/day
    // does not describe what a sub-hourly Routine consumes, even though the user
    // was shown a truthful cost estimate before enabling it.
    // Harmless today — billing is consumption-derived, so the meter charges what
    // actually ran. It becomes a real liability the moment the rate card becomes
    // the charge (migration Phase 5). MEASURE BEFORE THEN.
    task: 'high-frequency monitoring Routine (sub-hourly)',
    wu: 1,
    unit: 'per day',
    receive: 'unlimited runs within the day — watch anything, around the clock',
  },
  {
    task: 'overruns · retries · failovers · thinking depth',
    wu: 0,
    unit: 'always free',
    receive: 'variance is our risk, not your invoice',
  },
] as const;

/**
 * The published worked example — what a typical 2,200-PR0 month buys
 * (mirrors the Pricing Public sheet and the Sample Work Statement; every
 * line itemized on the monthly Work Statement).
 */
/*
 * The worked example — recut 2026-08-19 against the recalibrated rates; the
 * line items sum to exactly 2,200. At the old (5-13x overstated) rates the
 * same 2,200 pr0 bought only ~10 memos + ~50 documents + ~70 translations +
 * ~240 files + ~500 tasks, which is what /pricing had been telling buyers a
 * full month of an AI employee amounts to. It buys several times that.
 *
 * NUMERIC, not display strings (2026-08-19 owner directive: "easy to use
 * calculator with preloaded numbers"). This same array IS the calculator's
 * preset on /pricing and /subscription — `count` is the starting position of
 * each slider and `wuEach` its rate — so the worked example and the thing the
 * buyer drags can never disagree. Rendering adds the "~".
 *
 * INVARIANTS, enforced by scripts/check-public-pricing-sync.mjs:
 *   - every wuEach matches its WORK_RATE_CARD row
 *   - sum(count x wuEach) === MONTHLY_WU_PER_PRESTON exactly
 */
export const TYPICAL_MONTH = [
  { key: 'memo', count: 25, wuEach: 8, item: 'deep-research memos' },
  { key: 'document', count: 120, wuEach: 3, item: 'drafted documents' },
  { key: 'translation', count: 150, wuEach: 3, item: 'certified-style translations' },
  { key: 'file', count: 350, wuEach: 2, item: 'files processed' },
  { key: 'task', count: 490, wuEach: 1, item: 'tasks, Routines & voice minutes' },
] as const;

/** pr0 consumed by one worked-example line. */
export const typicalMonthLineWu = (row: { count: number; wuEach: number }): number =>
  row.count * row.wuEach;

/**
 * The most of ONE product a month can hold — 2,200 ÷ its rate. This is what
 * the old SINGLE_PRODUCT_CEILINGS list published as static prose ("≈275
 * research memos, ≈730 drafted documents, …"); it is now the maximum of each
 * calculator slider, so a buyer discovers it by dragging instead of reading a
 * second list of numbers. Same fact, one section instead of two.
 */
export const singleProductCeiling = (wuEach: number): number =>
  Math.floor(MONTHLY_WU_PER_PRESTON / wuEach);

/*
 * (SINGLE_PRODUCT_CEILINGS was removed 2026-08-19. It published the same four
 * numbers as static prose beside the worked month and the rate table — three
 * lists of the same arithmetic on one page. The ceilings are now the maximum
 * of each calculator slider, via singleProductCeiling() above.)
 */

/** Convert a user-charged dollar amount to displayed WU at the Standard Rate. */
export const usdToWu = (usd: number): number => usd / STANDARD_RATE_USD_PER_WU;

/** Convert WU back to dollars at the Standard Rate. */
export const wuToUsd = (wu: number): number => wu * STANDARD_RATE_USD_PER_WU;

/**
 * Format a WU quantity for display. WU live at human scale (a Prest0n month
 * is 2,200) so precision tapers with magnitude; tiny nonzero amounts floor
 * at "<0.01" rather than rendering long decimal tails.
 */
export function formatWu(value: number, showPrefix = false): string {
  if (!Number.isFinite(value)) return '0';
  const prefix = showPrefix && value > 0 ? '+' : value < 0 ? '-' : '';
  const abs = Math.abs(value);

  if (abs === 0) return '0';
  if (abs < 0.01) return `${prefix}<0.01`;
  if (abs < 100) return prefix + abs.toFixed(2).replace(/\.?0+$/, '');
  if (abs < 1000) return prefix + abs.toFixed(1).replace(/\.?0+$/, '');
  return (
    prefix +
    abs.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
  );
}

/**
 * DETAIL-surface formatter (2026-08-14 Miles directive: "more decimal places
 * in a strategic way"). Per-call rows — the ⓘ usage popup's per-generation
 * lines, activity drill-downs, expense line items — must show the real
 * quantity, never the "<0.01" floor: a single model call is often
 * 0.0005–0.005 pr0 and the floor made every cheap call look identical.
 *
 * Contract by magnitude:
 *   ≥ 100    → same taper as formatWu (headline territory)
 *   ≥ 0.01   → up to 3 decimals, trailing zeros trimmed
 *   < 0.01   → at least TWO significant figures (0.00042 renders as itself),
 *              capped at 8 decimals; true zero renders '0'
 *
 * HEADLINE surfaces (wallet balance, daily/monthly totals, plan meters) keep
 * formatWu's taper — clean numbers where precision is noise.
 */
export function formatWuDetail(value: number, showPrefix = false): string {
  if (!Number.isFinite(value)) return '0';
  const prefix = showPrefix && value > 0 ? '+' : value < 0 ? '-' : '';
  const abs = Math.abs(value);

  if (abs === 0) return '0';
  if (abs >= 100) return formatWu(value, showPrefix);
  if (abs >= 0.01) return prefix + abs.toFixed(3).replace(/\.?0+$/, '');
  const decimals = Math.min(8, -Math.floor(Math.log10(abs)) + 1);
  return prefix + abs.toFixed(decimals).replace(/0+$/, '').replace(/\.$/, '');
}

/** Detail formatter with the unit attached: "0.0004 pr0". */
export function formatWuDetailWithUnit(value: number, showPrefix = false): string {
  return `${formatWuDetail(value, showPrefix)} ${WU_LABEL}`;
}

/** Format with the unit attached: "142.5 WU". */
export function formatWuWithUnit(value: number, showPrefix = false): string {
  return `${formatWu(value, showPrefix)} ${WU_LABEL}`;
}

/** Format a charged dollar amount directly as WU: formatWuFromUsd(4.36) → "2 WU". */
export function formatWuFromUsd(usd: number, showPrefix = false): string {
  return formatWu(usdToWu(usd), showPrefix);
}
